import { useMemo, useState } from "react";
import { GradientText, Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { FlagCircle } from "./FlagCircle";
import type { DestinationOption } from "../api/catalog";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";
import { CloseLight } from "../assets/navIcons";


export interface SelectDestinationCurrencySlotProps {
  title: string;
  options: DestinationOption[];
  selectedCurrency: string;
  onSelect: (currencyCode: string) => void;
  onClose: () => void;
}

/** Currency-only picker: destination country and recipient remain pinned. */
export const SelectDestinationCurrencySlot: React.FC<
  SelectDestinationCurrencySlotProps
> = ({ title, options, selectedCurrency, onSelect, onClose }) => {
  const { t } = useTranslation();
  const { bodyCopy, backgroundBtn } = useStore().theme.whiteView;
  const [query, setQuery] = useState("");

  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((option) =>
      [option.countryName, option.countryCode, option.currencyCode].some(
        (field) => normalize(field).includes(q),
      ),
    );
  }, [options, query]);

  return (
    <section className="select-payout-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title
          title={title}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <input
        type="text"
        className="select-country-search"
        placeholder={t("transfer.searchCountry")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t("transfer.searchCountry")}
        autoComplete="off"
      />
      <ul className="select-payout-list">
        <ScrollFadeIndicator color={bodyCopy} />
        {filtered.length === 0 ? (
          <li className="select-country-empty">
            <GradientText textColor={bodyCopy}>
              {t("transfer.noCountriesFound")}
            </GradientText>
          </li>
        ) : null}
        {filtered.map((option) => {
          const selected = selectedCurrency === option.currencyCode;
          return (
            <li
              key={`${option.countryCode}-${option.currencyCode}`}
              className={`select-payout-option ${
                selected ? "selected" : ""
              }`.trim()}
              onClick={() => {
                onSelect(option.currencyCode);
                onClose();
              }}
              style={
                {
                  "--bgc": backgroundBtn,
                } as React.CSSProperties
              }
            >
              <section className="select-payout-icon">
                <FlagCircle
                  iso2={option.countryCode.toLowerCase()}
                  size={24}
                />
              </section>
              <GradientText
                textColor={bodyCopy}
                className="select-payout-label"
              >
                {option.countryName}
              </GradientText>
              <GradientText
                textColor={bodyCopy}
                className="select-payout-currency"
              >
                {option.currencyCode}
              </GradientText>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
